import { useEffect, useRef, useState } from "react";
import { api, type Driver } from "../api";
import PinDialog from "../components/PinDialog";


function AddDriverDialog({
  pin,
  onAdded,
  onCancel,
}: {
  pin: string;
  onAdded: (driver: Driver) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      onAdded(await api.createDriver(trimmed, pin));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add user");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-6"
      onClick={onCancel}
    >
      <div
        className="pop-in flex w-full max-w-xs flex-col gap-4 rounded-[20px] bg-white p-6 shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-[19px] font-bold tracking-[-0.02em]">Add new user</h2>
        <input
          ref={inputRef}
          autoFocus
          type="text"
          placeholder="User name"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="h-14 w-full rounded-[14px] bg-white px-5 text-center font-semibold outline-none placeholder:text-ink/40 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20"
        />
        {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}
        <div className="flex gap-2">
          <button onClick={onCancel} className="h-12 flex-1 rounded-[14px] bg-white font-bold border border-line text-[#3a1c25]">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!name.trim() || busy}
            className="h-12 flex-1 rounded-[14px] bg-rose-grad font-bold text-white disabled:opacity-50 shadow-button"
          >
            {busy ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DriverPicker({
  onPick,
  onBack,
  title = "Who's driving?",
  matsentralen = true,
}: {
  onPick: (driver: Driver) => void;
  onBack?: () => void;
  title?: string;
  matsentralen?: boolean; // the box tracker shows Matsentralen's logo; other tools don't
}) {
  const [drivers, setDrivers] = useState<Driver[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<{ step: "pin" } | { step: "name"; pin: string } | null>(
    null,
  );

  useEffect(() => {
    api
      .drivers()
      .then(setDrivers)
      .catch(() => setError("Couldn't load drivers. Check the connection and pull to retry."));
  }, []);

  const handleAdded = (driver: Driver) => {
    setDrivers((list) =>
      list && !list.some((d) => d.id === driver.id) ? [...list, driver] : list,
    );
    setAdding(null);
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-8 px-6">
      <div className="flex flex-col items-center gap-3">
        <button onClick={onBack} aria-label="Back to start" disabled={!onBack} className="active:scale-95">
          <img src="/logo.png" alt="Soulcake" className="h-16 w-auto" />
        </button>
        {matsentralen && <img src="/matsentralen.png" alt="Matsentralen" className="h-9" />}
      </div>
      <h1 className="text-[26px] font-bold tracking-[-0.02em] text-[#3a1c25]">{title}</h1>
      <div className="flex w-full flex-col gap-3">
        {drivers === null && !error && (
          <p className="text-center font-semibold text-ink/50">Loading drivers…</p>
        )}
        {error && <p className="text-center font-semibold text-danger">{error}</p>}
        {drivers?.map((driver, i) => (
          <button
            key={driver.id}
            onClick={() => onPick(driver)}
            className="pop-in flex h-16 w-full items-center gap-3 rounded-[18px] border border-line-soft bg-white px-4 text-left text-lg font-bold shadow-card transition-transform active:scale-[0.98]"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blush text-base text-rose-ink" aria-hidden>
              {driver.name.trim().charAt(0).toUpperCase()}
            </span>
            {driver.name}
          </button>
        ))}
        {drivers && (
          <>
            <hr className="border-t border-line" />
            <button
              onClick={() => setAdding({ step: "pin" })}
              className="h-14 w-full rounded-[14px] bg-white font-bold text-ink/70 transition-transform active:scale-95 border border-line"
            >
              + Add new user
            </button>
          </>
        )}
      </div>
      {onBack && (
        <button onClick={onBack} className="text-sm font-bold text-ink/50">
          ← Back to start
        </button>
      )}

      {adding?.step === "pin" && (
        <PinDialog
          title="Add new user"
          detail="Enter the PIN to add a user."
          confirmLabel="Continue"
          confirmBusyLabel="Checking…"
          confirmClass="bg-rose-grad text-white shadow-button"
          onConfirm={async (pin) => {
            await api.verifyPin(pin);
            setAdding({ step: "name", pin });
          }}
          onCancel={() => setAdding(null)}
        />
      )}
      {adding?.step === "name" && (
        <AddDriverDialog pin={adding.pin} onAdded={handleAdded} onCancel={() => setAdding(null)} />
      )}
    </div>
  );
}
